import { forwardRef } from 'react'
import { Message } from '../types'
import { MessageRenderer } from '../MessageRenderer'

interface MessageListProps {
  messages: Message[]
  isLoading: boolean
  isSearching: boolean
  webSearchStatus: {
    active: boolean
    query?: string
    url?: string
    domain?: string
    title?: string
    outputIndex?: number
  }
  onScroll: () => void
  messagesEndRef: React.RefObject<HTMLDivElement>
}

/**
 * Component for displaying the list of chat messages.
 * Includes loading indicators and scroll management.
 */
export const MessageList = forwardRef<HTMLDivElement, MessageListProps>(
  (
    {
      messages,
      isLoading,
      isSearching,
      webSearchStatus,
      onScroll,
      messagesEndRef,
    },
    ref
  ) => {
    return (
      <div
        ref={ref}
        onScroll={onScroll}
        className='flex-1 overflow-y-auto px-4 py-4'
      >
        {messages.length === 0 ? (
          <div className='flex h-full items-center justify-center'>
            {/* Empty state - no text */}
          </div>
        ) : (
          <div className='space-y-4'>
            {messages.map((message) => (
              <MessageRenderer key={message.id} message={message} />
            ))}

            {/* Local notes searching indicator */}
            {isSearching && (
              <div className='flex items-center gap-2'>
                <div className='inline-flex items-center space-x-1'>
                  <div className='h-2 w-2 animate-ping rounded-full bg-amber-500 dark:bg-amber-400'></div>
                  <div className='h-2 w-2 animate-ping rounded-full bg-amber-500 [animation-delay:0.2s] dark:bg-amber-400'></div>
                  <div className='h-2 w-2 animate-ping rounded-full bg-amber-500 [animation-delay:0.4s] dark:bg-amber-400'></div>
                </div>
                <span className='pl-1 text-xs text-amber-500 dark:text-amber-400'>
                  searching notes
                </span>
              </div>
            )}

            {/* Web search indicator */}
            {webSearchStatus.active && (
              <div className='ml-1 flex items-center'>
                <div className='inline-flex items-center space-x-1'>
                  <div className='h-2 w-2 animate-ping rounded-full bg-blue-500 dark:bg-blue-400'></div>
                  <div className='h-2 w-2 animate-ping rounded-full bg-blue-500 [animation-delay:0.2s] dark:bg-blue-400'></div>
                  <div className='h-2 w-2 animate-ping rounded-full bg-blue-500 [animation-delay:0.4s] dark:bg-blue-400'></div>
                </div>
                <span className='pl-2 text-xs text-blue-500 dark:text-blue-400'>
                  searching web
                </span>
              </div>
            )}

            {/* Loading indicator */}
            {isLoading && !webSearchStatus.active && (
              <div className='ml-1 flex items-start'>
                <div className='inline-flex items-center space-x-1'>
                  <div className='h-2 w-2 animate-bounce rounded-full bg-stone-400 [animation-delay:-0.3s]'></div>
                  <div className='h-2 w-2 animate-bounce rounded-full bg-stone-400 [animation-delay:-0.15s]'></div>
                  <div className='h-2 w-2 animate-bounce rounded-full bg-stone-400'></div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        )}
      </div>
    )
  }
)

MessageList.displayName = 'MessageList'
